// Module Contrats — widget « Consommations d'énergie » du dashboard eco.
//
// Se nourrit de /api/contracts/consumption-summary/ : par contrat d'énergie
// (électricité, gaz, eau, chauffage), la série des consommations extraites
// des factures par l'IA. Même trame que EcoTrendChart (LAP-752) : raw SVG
// (pas de lib de charts), auto-fetch, empty state guidé vers /contrats.
import { Box, Card, Button } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import WTypography from '../../../../Components/whiteTypography'
import { contratsApi } from '../../../Contrats'

// Couleurs alignées sur Contrats/components/shared.js::CONTRACT_TYPES.
const TYPE_COLORS = {
  electricity: '#ffb547',
  gas: '#5aa7ff',
  water: '#3ddcd0',
  heating: '#ff7847',
}

function EnergyConsumptionChart() {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const [contracts, setContracts] = useState([])
  const [hasData, setHasData] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)

  useEffect(() => {
    let cancelled = false
    contratsApi
      .getConsumptionSummary()
      .then((d) => {
        if (cancelled) return
        const list = (d?.contracts || []).filter((c) => (c.series || []).length > 0)
        setContracts(list)
        setHasData(!!d?.has_data)
        setSelectedId((prev) => prev ?? list[0]?.contract_id ?? null)
      })
      .catch(() => {
        if (!cancelled) {
          setContracts([])
          setHasData(false)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selected =
    contracts.find((c) => c.contract_id === selectedId) || contracts[0] || null
  const series = useMemo(
    () => (selected?.series || []).filter((p) => p.consumption_value != null),
    [selected]
  )
  const color = TYPE_COLORS[selected?.contract_type] || '#ffb547'
  const unit = series.find((p) => p.consumption_unit)?.consumption_unit || ''
  const anomaliesCount = series.reduce(
    (n, p) => n + (Array.isArray(p.anomalies) ? p.anomalies.length : 0),
    0
  )

  // Même viewBox normalisé que EcoTrendChart pour un rendu cohérent.
  const W = 480
  const H = 230
  const padRight = 30
  const padBottom = 30
  const padTop = 20

  const maxY = Math.max(1, ...series.map((p) => Number(p.consumption_value) || 0))
  const xStep = series.length > 1 ? (W - padRight) / (series.length - 1) : 0
  const yScale = (v) => H - padBottom - ((Number(v) || 0) / maxY) * (H - padTop - padBottom)
  const xScale = (i) => i * xStep

  const linePath = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(p.consumption_value)}`)
    .join(' ')
  const areaPath = series.length
    ? `${linePath} L${xScale(series.length - 1)},${H - padBottom} L0,${H - padBottom} Z`
    : ''

  const fmtPeriod = (p) =>
    p?.period_end ? String(p.period_end).slice(2, 7) : ''

  return (
    <Card className="dashboard_card__root1">
      <Box className="dashboard_card__header1" style={{ marginBottom: 8 }}>
        <WTypography component="span" variant="h6">
          <BoltRoundedIcon
            sx={{ fontSize: 18, color: '#ffb547', verticalAlign: 'text-bottom', mr: 0.5 }}
          />
          {t('dashboard.energyConsumptionTitle', 'Consommations d\'énergie')}
        </WTypography>
        {/* Sélecteur de contrat — même style que le sélecteur de période LAP-752 */}
        {contracts.length > 1 && (
          <Box display="flex" gap={1} flexWrap="wrap" className="no-drag">
            {contracts.map((c) => (
              <span
                key={c.contract_id}
                onClick={() => setSelectedId(c.contract_id)}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  color:
                    c.contract_id === selected?.contract_id
                      ? TYPE_COLORS[c.contract_type] || '#ffb547'
                      : 'rgba(255,255,255,0.5)',
                  background:
                    c.contract_id === selected?.contract_id
                      ? 'rgba(255,181,71,0.16)'
                      : 'transparent',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {c.name}
              </span>
            ))}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box height={230} display="flex" alignItems="center" justifyContent="center">
          <WTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('dashboard.loading', 'Chargement…')}
          </WTypography>
        </Box>
      ) : !hasData || series.length === 0 ? (
        // Empty state guidé — même parcours que EcoTrendChart : expliquer
        // quoi faire et envoyer l'utilisateur vers le module Contrats.
        <Box
          height={260}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={2}
        >
          <WTypography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
            {t('dashboard.energyEmptyTitle', 'Suivez vos consommations d\'énergie')}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 400, mb: 2, fontSize: 13 }}
          >
            {t(
              'dashboard.energyEmptyDesc',
              'Ajoutez vos contrats (électricité, gaz, eau…), attachez vos factures et laissez l\'IA en extraire les consommations pour alimenter cette courbe.'
            )}
          </WTypography>
          <Button
            size="small"
            startIcon={<BoltRoundedIcon />}
            onClick={() => navigate('/contrats')}
            sx={emptyCtaStyle}
            className="no-drag"
          >
            {t('dashboard.energyEmptyCta', 'Mes Contrats')}
          </Button>
        </Box>
      ) : (
        <>
          {/* Ligne d'infos : dernière conso + anomalies détectées par l'IA */}
          <Box
            display="flex"
            gap={2}
            mb={1}
            alignItems="center"
            style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}
          >
            <span>
              <span style={{ color, fontWeight: 700 }}>
                {Number(series[series.length - 1].consumption_value).toLocaleString('fr-FR')}
                {unit ? ` ${unit}` : ''}
              </span>{' '}
              {t('dashboard.energyLastPeriod', 'sur la dernière facture')}
            </span>
            {anomaliesCount > 0 && (
              <span style={{ color: '#ffcdb8', display: 'inline-flex', alignItems: 'center' }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {anomaliesCount}{' '}
                {t('dashboard.energyAnomalies', 'anomalie(s) détectée(s)')}
              </span>
            )}
            <span
              onClick={() => navigate('/contrats')}
              className="no-drag"
              style={{ marginLeft: 'auto', color: '#ffb547', cursor: 'pointer' }}
            >
              {t('dashboard.energySeeContracts', 'Voir mes contrats →')}
            </span>
          </Box>

          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 230, display: 'block' }}>
            <defs>
              <linearGradient id="energyConsGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.45" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Gridlines */}
            {[0.25, 0.5, 0.75].map((r) => (
              <line
                key={r}
                x1={0}
                y1={padTop + r * (H - padTop - padBottom)}
                x2={W}
                y2={padTop + r * (H - padTop - padBottom)}
                stroke="rgba(255,255,255,0.06)"
              />
            ))}

            {/* Aire + courbe de consommation */}
            {areaPath && <path d={areaPath} fill="url(#energyConsGrad)" />}
            <path
              d={linePath}
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Points par facture */}
            {series.map((p, i) => (
              <circle
                key={i}
                cx={xScale(i)}
                cy={yScale(p.consumption_value)}
                r={i === series.length - 1 ? 4 : 2.5}
                fill={color}
                stroke={i === series.length - 1 ? '#fff' : 'none'}
                strokeWidth="1.5"
              />
            ))}

            {/* Libellés X (première / médiane / dernière période) */}
            <g fill="rgba(255,255,255,0.4)" fontSize="10">
              <text x={0} y={H - 8}>
                {fmtPeriod(series[0])}
              </text>
              {series.length > 2 && (
                <text x={W / 2} y={H - 8} textAnchor="middle">
                  {fmtPeriod(series[Math.floor(series.length / 2)])}
                </text>
              )}
              <text x={W - padRight} y={H - 8} textAnchor="end">
                {fmtPeriod(series[series.length - 1])}
              </text>
            </g>

            {/* Max Y */}
            <text x={W - padRight} y={padTop - 6} textAnchor="end" fill="rgba(255,255,255,0.4)" fontSize="10">
              {maxY.toLocaleString('fr-FR')}
              {unit ? ` ${unit}` : ''}
            </text>
          </svg>
        </>
      )}
    </Card>
  )
}

const emptyCtaStyle = {
  textTransform: 'none',
  color: '#ffdca8',
  background: 'rgba(255,181,71,0.16)',
  border: '1px solid rgba(255,181,71,0.4)',
  fontSize: 12,
  fontWeight: 600,
  px: 1.5,
  '&:hover': { background: 'rgba(255,181,71,0.28)', borderColor: '#ffb547' },
}

export default EnergyConsumptionChart
